import { useEffect, useReducer, useRef, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import {
  Camera,
  ImagePlus,
  Loader2,
  NotebookPen,
  RotateCcw,
  X,
} from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { imageFileValidationError, uploadImagePipeline } from "../../lib/image-upload";
import { useLoggingFlow } from "../../lib/logging-flow";
import { isMobileDevice } from "../../lib/photo-picker";
import { CreditDrawer } from "../CreditDrawer";
import {
  INITIAL_PHOTO_FLOW_STATE,
  buildManualReviewPayload,
  buildPhotoReviewPayload,
  describeCameraError,
  photoFlowReducer,
  runMealImageAnalysis,
} from "./photo-flow";
import type { PhotoFlowSource, PhotoFlowState } from "./photo-flow";

// Photo logging flow (plan todo 13): capture/upload -> optional note ->
// analyze -> unified Meal Review. Every capture lands on Meal Review — this
// flow NEVER logs directly. Upload logic is shared with Chat via
// src/lib/image-upload.ts; INSUFFICIENT_CREDITS opens the CreditDrawer;
// ANALYSIS_FAILED offers Retry / Enter manually (blank review).

export interface PhotoLogFlowProps {
  readonly kind: PhotoFlowSource;
  readonly demoMode?: boolean;
  readonly onClose: () => void;
  // Photo chosen up-front via the always-mounted native picker (photo-picker).
  // When present the pick/auto-open step is skipped and the upload starts
  // immediately.
  readonly initialFile?: File;
  // Test hook: lets renderToStaticMarkup suites render a specific step.
  readonly initialState?: PhotoFlowState;
}

const TITLE_BY_KIND: Record<PhotoFlowSource, string> = {
  photo: "Take meal photo",
  upload: "Upload meal photo",
};

export function PhotoLogFlow({ kind, demoMode = false, onClose, initialFile, initialState }: PhotoLogFlowProps) {
  const [state, dispatch] = useReducer(
    photoFlowReducer,
    initialState ??
      (initialFile !== undefined && !demoMode
        ? {
            ...INITIAL_PHOTO_FLOW_STATE,
            phase: "uploading",
            file: initialFile,
            fileName: initialFile.name,
          }
        : INITIAL_PHOTO_FLOW_STATE),
  );
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [pickCameraError, setPickCameraError] = useState<string | null>(null);
  const [mobileDevice] = useState(isMobileDevice);
  // Tracks the auto camera attempt (kind="photo" without a pre-chosen file).
  // While it is pending the pick page stays hidden behind an "Opening
  // camera…" state so the two never render together; the pick page appears
  // only after the attempt concludes without a file (picker cancelled,
  // camera failed). Passing the initialState test hook means "render this
  // exact step", so it starts concluded.
  const [autoCameraPending, setAutoCameraPending] = useState(
    () => kind === "photo" && initialFile === undefined && initialState === undefined,
  );

  const { startFlow } = useLoggingFlow();
  const limits = useQuery(api.images.getUploadLimits, demoMode ? "skip" : {});
  const generateUploadUrl = useMutation(api.images.generateUploadUrl);
  const claimUpload = useMutation(api.images.claimUpload);
  const analyzeMealImage = useAction(api.analyze.analyzeMealImage);

  const libraryInputRef = useRef<HTMLInputElement>(null);
  const captureInputRef = useRef<HTMLInputElement>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const cameraActiveRef = useRef(false);
  const uploadBusyRef = useRef(false);
  const previewUrlRef = useRef<string | null>(null);
  const autoOpenedLibraryRef = useRef(initialFile !== undefined);
  const autoOpenedCameraRef = useRef(initialFile !== undefined);
  const initialUploadStartedRef = useRef(false);

  // A file already chosen via the native picker skips the pick step: start
  // the upload as soon as the limits query resolves.
  useEffect(() => {
    if (initialFile === undefined || demoMode || initialUploadStartedRef.current) return;
    if (limits === undefined) return;
    initialUploadStartedRef.current = true;
    console.log("[Frontend][PhotoLogFlow] Uploading photo chosen via native picker", {
      kind,
      fileName: initialFile.name,
    });
    startUpload(initialFile);
  }, [initialFile, demoMode, limits, kind]);

  // The photo flow starts at the camera step: open it immediately (native
  // capture sheet on mobile, getUserMedia modal on desktop). While the
  // attempt is pending the pick screen is NOT rendered (no double UI); it
  // appears once the attempt concludes without a file — the user cancelled
  // the native sheet (input `cancel` event, with a window-focus fallback for
  // older WebViews) or the camera failed.
  useEffect(() => {
    if (kind !== "photo" || autoOpenedCameraRef.current) return;
    autoOpenedCameraRef.current = true;
    console.log("[Frontend][PhotoLogFlow] Auto-opening camera for photo flow", { mobileDevice });
    if (!mobileDevice) {
      // Desktop: the getUserMedia modal is the attempt. Pending ends when it
      // settles — success keeps the modal open; failure reveals the pick
      // page (behind the modal's error state) with the error copy attached.
      void openCamera().finally(() => setAutoCameraPending(false));
      return;
    }
    const input = captureInputRef.current;
    if (input === null) {
      setAutoCameraPending(false);
      return;
    }
    let concluded = false;
    const conclude = (reason: string) => {
      if (concluded) return;
      concluded = true;
      console.log("[Frontend][PhotoLogFlow] Auto camera attempt concluded without a file", { reason });
      window.removeEventListener("focus", handleWindowFocus);
      setAutoCameraPending(false);
    };
    const handleWindowFocus = () => {
      // Focus-return heuristic for pickers without a `cancel` event: if the
      // window regains focus and no file arrived shortly after, the user
      // dismissed the native sheet.
      window.setTimeout(() => {
        if (!concluded && (input.files === null || input.files.length === 0)) {
          conclude("focus-return-no-file");
        }
      }, 400);
    };
    input.addEventListener("cancel", () => conclude("picker-cancelled"), { once: true });
    window.addEventListener("focus", handleWindowFocus);
    try {
      input.click();
    } catch (error: unknown) {
      // jsdom/legacy WebViews can throw on programmatic clicks; the pick
      // screen becomes the manual path once the attempt concludes.
      console.warn("[Frontend][PhotoLogFlow] Auto-open camera input failed", {
        error: error instanceof Error ? error.message : String(error),
      });
      conclude("click-failed");
    }
    return () => {
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [kind, mobileDevice]);

  // The upload flow starts at the library-input step (todo 13 contract): try
  // to open the picker immediately; the option buttons below stay as the
  // always-available fallback when the browser blocks the programmatic click.
  useEffect(() => {
    if (kind !== "upload" || autoOpenedLibraryRef.current) return;
    autoOpenedLibraryRef.current = true;
    console.log("[Frontend][PhotoLogFlow] Auto-opening library picker for upload flow");
    libraryInputRef.current?.click();
  }, [kind]);

  // Full camera + preview cleanup on unmount.
  useEffect(() => {
    return () => {
      cameraActiveRef.current = false;
      cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
      if (previewUrlRef.current !== null) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    };
  }, []);

  const captureEnabled = !demoMode && limits !== undefined;

  const startUpload = (file: File) => {
    if (limits === undefined) {
      console.warn("[Frontend][PhotoLogFlow] Upload blocked; limits not loaded");
      return;
    }
    setPickCameraError(null);
    const validationMessage = imageFileValidationError(file, limits);
    if (validationMessage !== null) {
      console.warn("[Frontend][PhotoLogFlow] File rejected by validation", {
        fileName: file.name,
        reason: validationMessage,
      });
      dispatch({ type: "fileRejected", message: validationMessage });
      return;
    }
    if (uploadBusyRef.current) return;
    uploadBusyRef.current = true;
    dispatch({ type: "filePicked", file });
    console.log("[Frontend][PhotoLogFlow] Photo upload started", {
      kind,
      fileName: file.name,
      fileSize: file.size,
    });
    void uploadImagePipeline(file, { generateUploadUrl, claimUpload })
      .then(({ storageId, previewUrl }) => {
        if (previewUrlRef.current !== null) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = previewUrl;
        dispatch({ type: "uploadSucceeded", storageId, previewUrl });
        console.log("[Frontend][PhotoLogFlow] Photo upload claimed", { fileName: file.name });
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        dispatch({ type: "uploadFailed", message });
        console.error("[Frontend][PhotoLogFlow] Photo upload failed", {
          fileName: file.name,
          error: message,
        });
      })
      .finally(() => {
        uploadBusyRef.current = false;
      });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setAutoCameraPending(false);
    startUpload(file);
  };

  const startAnalysis = () => {
    const storageId = state.storageId;
    if (storageId === null) return;
    dispatch({ type: "analyzeStarted" });
    const note = state.note.trim();
    console.log("[Frontend][PhotoLogFlow] Analysis requested", { kind, hasNote: note.length > 0 });
    void runMealImageAnalysis(analyzeMealImage, {
      storageId: storageId as Id<"_storage">,
      ...(note.length > 0 ? { note } : {}),
    }).then((outcome) => {
      if (outcome.ok) {
        startFlow("review", buildPhotoReviewPayload(outcome.analysis, storageId, kind));
        return;
      }
      dispatch({ type: "analyzeFailed", message: outcome.message, credits: outcome.credits });
    });
  };

  const stopCameraStream = () => {
    cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    cameraStreamRef.current = null;
  };

  const closeCamera = () => {
    cameraActiveRef.current = false;
    stopCameraStream();
    setCameraOpen(false);
    setCameraError(null);
  };

  const openCamera = async () => {
    setCameraOpen(true);
    setCameraError(null);
    setPickCameraError(null);
    cameraActiveRef.current = true;
    console.log("[Frontend][PhotoLogFlow] Camera modal opened");
    const mediaDevicesSupported =
      typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);
    if (!mediaDevicesSupported) {
      const { kind: errorKind, message } = describeCameraError(null, false);
      setCameraError(message);
      setPickCameraError(message);
      console.warn("[Frontend][PhotoLogFlow] Camera unsupported in this browser", { errorKind });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (!cameraActiveRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      cameraStreamRef.current = stream;
      const video = cameraVideoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play().catch(() => undefined);
      }
    } catch (error: unknown) {
      const { kind: errorKind, message } = describeCameraError(error, true);
      setCameraError(message);
      setPickCameraError(message);
      console.error("[Frontend][PhotoLogFlow] Camera start failed", {
        errorKind,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const handleCameraCapture = () => {
    const video = cameraVideoRef.current;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
      setCameraError("Camera is not ready yet. Try again in a moment.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      setCameraError("Could not capture photo. Try again.");
      return;
    }
    context.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setCameraError("Could not capture photo. Try again.");
          return;
        }
        const file = new File([blob], `camera-${Date.now()}.jpg`, { type: "image/jpeg" });
        console.log("[Frontend][PhotoLogFlow] Camera frame captured", {
          fileName: file.name,
          fileSize: file.size,
        });
        closeCamera();
        startUpload(file);
      },
      "image/jpeg",
      0.92,
    );
  };

  const handleEnterManually = () => {
    console.log("[Frontend][PhotoLogFlow] Analysis declined; opening blank review", { kind });
    startFlow("review", buildManualReviewPayload(state.storageId, kind));
  };

  const handlePickDifferent = () => {
    if (previewUrlRef.current !== null) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPickCameraError(null);
    dispatch({ type: "pickDifferentPhoto" });
  };

  const optionButtonClass =
    "flex min-h-[56px] w-full items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3 text-base font-medium text-text transition-all duration-200 ease-out active:scale-[0.98] active:bg-muted disabled:opacity-50 select-none";

  const pickErrorMessage = state.errorMessage ?? pickCameraError;

  return (
    <div data-testid="photo-flow" className="fixed inset-0 z-40 flex flex-col bg-muted">
      <header className="w-full shrink-0 border-b border-border bg-surface pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex max-w-xl items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-lg font-semibold text-text">{TITLE_BY_KIND[kind]}</h1>
            <p className="text-xs text-text-muted">We analyze the photo, you review before logging</p>
          </div>
          <button
            type="button"
            data-testid="photo-flow-close"
            aria-label="Close photo flow"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
          >
            <X size={20} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto pb-[max(env(safe-area-inset-bottom),1rem)]">
        <div className="mx-auto flex max-w-xl flex-col gap-3 px-4 py-4">
          {state.phase === "pick" && autoCameraPending && (
            <div
              data-testid="photo-flow-opening-camera"
              role="status"
              className="flex flex-col items-center gap-4 rounded-2xl border border-border bg-surface px-4 py-10 shadow-sm"
            >
              <Loader2 size={26} className="animate-spin text-accent" aria-hidden="true" />
              <p className="text-sm font-medium text-text">Opening camera…</p>
              <button
                type="button"
                data-testid="photo-flow-opening-cancel"
                onClick={onClose}
                className="min-h-[44px] rounded-xl px-4 py-2 text-sm font-medium text-text-muted transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
              >
                Cancel
              </button>
            </div>
          )}

          {state.phase === "pick" && !autoCameraPending && (
            <>
              {pickErrorMessage !== null && (
                <p
                  role="alert"
                  data-testid="photo-flow-error"
                  className="rounded-2xl bg-danger/10 px-4 py-3 text-sm font-medium text-danger"
                >
                  {pickErrorMessage}
                </p>
              )}
              <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
                <p className="text-sm text-text-muted">
                  {kind === "photo"
                    ? "Cancel any time — or pick from your library instead."
                    : "Cancelled? No problem — pick a meal photo from your library below."}
                </p>
                <div className="mt-3 flex flex-col gap-2">
                  {kind === "photo" && mobileDevice && (
                    <button
                      type="button"
                      data-testid="photo-flow-take-photo"
                      disabled={!captureEnabled}
                      onClick={() => captureInputRef.current?.click()}
                      className={optionButtonClass}
                    >
                      <Camera size={22} className="text-text-muted" aria-hidden="true" />
                      Take photo
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="photo-flow-choose-library"
                    disabled={!captureEnabled}
                    onClick={() => libraryInputRef.current?.click()}
                    className={optionButtonClass}
                  >
                    <ImagePlus size={22} className="text-text-muted" aria-hidden="true" />
                    {kind === "photo" ? "Choose from library" : "Upload a photo"}
                  </button>
                  {kind === "photo" && !mobileDevice && (
                    <button
                      type="button"
                      data-testid="photo-flow-use-camera"
                      disabled={!captureEnabled}
                      onClick={() => void openCamera()}
                      className={optionButtonClass}
                    >
                      <Camera size={22} className="text-text-muted" aria-hidden="true" />
                      Use camera
                    </button>
                  )}
                </div>
                {demoMode && (
                  <p data-testid="photo-flow-demo-note" className="mt-3 text-xs text-text-muted">
                    Demo mode — sign in to upload and analyze photos.
                  </p>
                )}
              </div>
            </>
          )}

          {state.phase === "uploading" && (
            <div
              data-testid="photo-flow-uploading"
              className="flex items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-4 shadow-sm"
            >
              <Loader2 size={22} className="animate-spin text-accent" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-text">Uploading photo…</p>
                <p className="truncate text-xs text-text-muted">{state.fileName ?? "photo"}</p>
              </div>
            </div>
          )}

          {state.phase === "upload-error" && (
            <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
              <p role="alert" data-testid="photo-flow-error" className="text-sm font-medium text-danger">
                Could not upload {state.fileName ?? "the photo"}. {state.errorMessage ?? ""}
              </p>
              <div className="mt-3 flex flex-col gap-2">
                <button
                  type="button"
                  data-testid="photo-flow-retry-upload"
                  onClick={() => {
                    if (state.file !== null) startUpload(state.file);
                  }}
                  className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 text-base font-semibold text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] select-none"
                >
                  <RotateCcw size={18} aria-hidden="true" />
                  Retry upload
                </button>
                <button
                  type="button"
                  data-testid="photo-flow-change-photo"
                  onClick={handlePickDifferent}
                  className="min-h-[44px] text-sm font-medium text-text-muted transition-opacity active:opacity-80 select-none"
                >
                  Choose a different photo
                </button>
              </div>
            </div>
          )}

          {state.phase === "note" && (
            <div className="flex flex-col gap-3">
              {state.previewUrl !== null && (
                <img
                  src={state.previewUrl}
                  alt="Selected meal"
                  data-testid="photo-flow-note-preview"
                  className="aspect-[4/3] w-full rounded-2xl border border-border object-cover"
                />
              )}
              <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
                <label htmlFor="photo-flow-note" className="flex items-center gap-2 text-sm font-semibold text-text">
                  <NotebookPen size={16} className="text-text-muted" aria-hidden="true" />
                  Anything to add? <span className="font-normal text-text-muted">(optional)</span>
                </label>
                <textarea
                  id="photo-flow-note"
                  data-testid="photo-flow-note-input"
                  rows={2}
                  value={state.note}
                  onChange={(event) => dispatch({ type: "noteChanged", note: event.target.value })}
                  placeholder="e.g. homemade chicken karahi, half plate"
                  className="mt-2 w-full resize-none rounded-xl border border-border bg-muted px-4 py-3 text-base text-text placeholder:text-text-muted caret-accent focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                />
                <button
                  type="button"
                  data-testid="photo-flow-analyze"
                  onClick={startAnalysis}
                  className="mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 text-base font-semibold text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] select-none"
                >
                  Analyze photo
                </button>
                <button
                  type="button"
                  data-testid="photo-flow-change-photo"
                  onClick={handlePickDifferent}
                  className="mt-2 min-h-[44px] w-full text-sm font-medium text-text-muted transition-opacity active:opacity-80 select-none"
                >
                  Choose a different photo
                </button>
              </div>
            </div>
          )}

          {state.phase === "analyzing" && (
            <div
              data-testid="photo-flow-analyzing"
              role="status"
              className="rounded-2xl border border-border bg-surface p-4 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <Loader2 size={22} className="animate-spin text-accent" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-text">Analyzing your meal…</p>
                  <p className="text-xs text-text-muted">Identifying items and estimating nutrition</p>
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-2" aria-hidden="true">
                <div className="h-10 animate-pulse rounded-xl bg-muted" />
                <div className="h-10 animate-pulse rounded-xl bg-muted" style={{ animationDelay: "150ms" }} />
                <div className="h-10 w-2/3 animate-pulse rounded-xl bg-muted" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}

          {state.phase === "analysis-error" && (
            <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
              <p role="alert" data-testid="photo-flow-error" className="text-sm font-medium text-danger">
                We could not analyze this photo. Your photo is still here — try again or enter the meal yourself.
              </p>
              <div className="mt-3 flex flex-col gap-2">
                <button
                  type="button"
                  data-testid="photo-flow-retry-analysis"
                  onClick={startAnalysis}
                  className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 text-base font-semibold text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] select-none"
                >
                  <RotateCcw size={18} aria-hidden="true" />
                  Retry analysis
                </button>
                <button
                  type="button"
                  data-testid="photo-flow-enter-manual"
                  onClick={handleEnterManually}
                  className="flex min-h-[48px] w-full items-center justify-center rounded-xl border border-border bg-surface px-4 text-base font-semibold text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
                >
                  Enter manually
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        data-testid="photo-flow-file-input"
        onChange={handleInputChange}
        className="hidden"
      />
      <input
        ref={captureInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        data-testid="photo-flow-camera-input"
        onChange={handleInputChange}
        className="hidden"
      />

      {cameraOpen && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Camera capture" data-testid="photo-flow-camera-modal">
          <button
            type="button"
            aria-label="Dismiss"
            className="absolute inset-0 bg-black/60"
            onClick={closeCamera}
          />
          <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-xl rounded-t-2xl border-t border-border bg-surface px-5 pt-4 pb-[max(env(safe-area-inset-bottom),1.25rem)]">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-text">Take a photo</h3>
              <button
                type="button"
                aria-label="Close camera"
                data-testid="photo-flow-camera-cancel"
                onClick={closeCamera}
                className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted active:bg-muted active:scale-[0.98] select-none"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>
            {cameraError !== null ? (
              <div className="mt-3 flex flex-col gap-3">
                <p role="alert" className="text-sm text-danger" data-testid="photo-flow-camera-error">
                  {cameraError}
                </p>
                <button
                  type="button"
                  data-testid="photo-flow-camera-fallback"
                  onClick={() => {
                    closeCamera();
                    libraryInputRef.current?.click();
                  }}
                  className="flex min-h-[48px] w-full items-center justify-center rounded-xl border border-border bg-surface px-4 text-base font-semibold text-text active:bg-muted active:scale-[0.98] select-none"
                >
                  Upload a photo
                </button>
              </div>
            ) : (
              <>
                <video
                  ref={cameraVideoRef}
                  autoPlay
                  playsInline
                  muted
                  data-testid="photo-flow-camera-preview"
                  className="mt-3 aspect-[4/3] w-full rounded-xl bg-black object-cover"
                />
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    data-testid="photo-flow-camera-capture"
                    onClick={handleCameraCapture}
                    className="flex min-h-[48px] flex-1 items-center justify-center rounded-xl bg-accent px-4 text-base font-semibold text-white active:bg-accent-dark active:scale-[0.98] select-none"
                  >
                    Capture
                  </button>
                  <button
                    type="button"
                    data-testid="photo-flow-camera-cancel-btn"
                    onClick={closeCamera}
                    className="flex min-h-[48px] items-center justify-center rounded-xl border border-border bg-surface px-4 text-base font-semibold text-text active:bg-muted active:scale-[0.98] select-none"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {state.creditDrawerOpen && (
        <CreditDrawer onClose={() => dispatch({ type: "creditDrawerClosed" })} />
      )}
    </div>
  );
}

export default PhotoLogFlow;
